import { Injectable, inject } from '@angular/core';
import { ConfigService } from '../../api/services/config.service';
import { TenantConfiguration } from '../../api/models/tenant-configuration';
import { catchError, distinctUntilChanged, map, Observable, of, shareReplay, switchMap, timer } from 'rxjs';

export interface TenantConfig {
  name: string | undefined;
  ssoEnabled: boolean | undefined;
  allowSuppliersToInviteCollaegue?: boolean;
  ssoDomains?: string[] | null;
  externalUserManagementEnabled: boolean;
  sectionBasedReviewEnabled: boolean;
  requestsHomepageEnabled: boolean;
  maintenanceMode: boolean;
  verificationCachingEnabled: boolean;
  verificationValidityDays: number | null;
  feedbackEnabled: boolean;
  feedbackPortalId: string | null;
  webhooksEnabled: boolean;
  mfaRequired: boolean;
}

/** Synchronous form of TenantService.isSsoEmail for callers that already hold the config (grids mapping rows). */
export function isSsoEmailForConfig(cfg: TenantConfig | null | undefined, email: string | null | undefined): boolean {
  if (!email || !cfg?.ssoEnabled || !cfg.ssoDomains || cfg.ssoDomains.length === 0) {
    return false;
  }
  const domain = email.split('@').pop()?.toLowerCase();
  return !!domain && cfg.ssoDomains.includes(domain);
}

/** Mirrors the backend's SsoUtils.isExternallyManaged: only admin/standard users are blocked from
 * self-managing their profile once externalUserManagementEnabled is on and their email domain is
 * SSO-linked. A plain isSsoEmailForConfig check (ssoEnabled + domain only) would wrongly suppress
 * this for suppliers, or for any user when externalUserManagementEnabled is off, even though the
 * backend still permits self-service updates for them (#58537). */
export function isExternallyManagedUser(
  user: { email?: string | null; accessControl?: string | null } | null | undefined,
  cfg: TenantConfig | null | undefined
): boolean {
  if (!user?.email || !cfg?.ssoEnabled || !cfg.externalUserManagementEnabled || !cfg.ssoDomains?.length) {
    return false;
  }
  if (user.accessControl !== 'admin' && user.accessControl !== 'standard') return false;
  const domain = user.email.split('@').pop()?.toLowerCase();
  return !!domain && cfg.ssoDomains.includes(domain);
}

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly configService = inject(ConfigService);

  private static readonly CONFIG_POLL_INTERVAL = 120_000;

  private _tenant: string;

  private readonly polledApiConfig$: Observable<TenantConfiguration | null>;

  public readonly config$: Observable<TenantConfig>;
  public readonly maintenanceMode$: Observable<boolean>;

  constructor() {
    this._tenant = this.extractTenantKey(window.location.hostname);

    this.polledApiConfig$ = timer(0, TenantService.CONFIG_POLL_INTERVAL).pipe(
      switchMap(() => this.configService.getTenantConfig({ tenantId: this._tenant }).pipe(catchError(() => of(null)))),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.config$ = this.polledApiConfig$.pipe(
      map((api) => this.mapConfig(api)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.maintenanceMode$ = this.config$.pipe(
      map((cfg) => cfg.maintenanceMode),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private mapConfig(api: TenantConfiguration | null): TenantConfig {
    return {
      name: api?.tenantId ?? this._tenant,
      ssoEnabled: api?.ssoEnabled,
      allowSuppliersToInviteCollaegue: api?.allowSuppliersToInviteColleague ?? false,
      // Lowercased once so every domain check matches the backend's case-insensitive comparison.
      ssoDomains: api?.ssoDomains?.filter((d) => typeof d === 'string').map((d) => d.toLowerCase()) ?? null,
      externalUserManagementEnabled: api?.externalUserManagementEnabled ?? false,
      sectionBasedReviewEnabled: api?.sectionBasedReviewEnabled ?? false,
      requestsHomepageEnabled: api?.requestsHomepageEnabled ?? false,
      maintenanceMode: api?.maintenanceMode ?? false,
      verificationCachingEnabled: api?.verificationCachingEnabled ?? false,
      verificationValidityDays: api?.verificationValidityDays ?? null,
      feedbackEnabled: api?.feedbackEnabled ?? false,
      feedbackPortalId: api?.feedbackPortalId ?? null,
      webhooksEnabled: api?.webhooksEnabled ?? false,
      mfaRequired: api?.mfaRequired ?? false,
    };
  }

  get tenant(): string {
    return this._tenant;
  }

  isSsoEnabled(): Observable<boolean | undefined> {
    return this.config$.pipe(map((cfg) => cfg.ssoEnabled));
  }

  isSsoEmail(email: string | null | undefined): Observable<boolean> {
    return this.config$.pipe(map((cfg) => isSsoEmailForConfig(cfg, email)));
  }

  useInviteColleagueLabel(): Observable<boolean> {
    return this.config$.pipe(map((cfg) => !!cfg.allowSuppliersToInviteCollaegue));
  }

  isSectionBasedReviewEnabled(): Observable<boolean> {
    return this.config$.pipe(map((cfg) => cfg.sectionBasedReviewEnabled));
  }

  isRequestsHomepageEnabled(): Observable<boolean> {
    return this.config$.pipe(map((cfg) => cfg.requestsHomepageEnabled));
  }

  isWebhooksEnabled(): Observable<boolean> {
    return this.config$.pipe(map((cfg) => cfg.webhooksEnabled));
  }

  isMfaRequired(): Observable<boolean> {
    return this.config$.pipe(map((cfg) => cfg.mfaRequired));
  }

  /** MFA is enforced for a user when the tenant requires it and the user isn't SSO — SSO users are always exempt. */
  isMfaRequiredForUser(email: string | null | undefined): Observable<boolean> {
    return this.config$.pipe(
      switchMap((cfg) => {
        if (!cfg.mfaRequired) {
          return of(false);
        }
        return this.isSsoEmail(email).pipe(map((isSso) => !isSso));
      })
    );
  }

  isMaintenanceMode(): Observable<boolean> {
    return this.maintenanceMode$;
  }

  isVerificationCachingEnabled(): Observable<boolean> {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('slm.forceVerificationCaching') === '1') {
      return of(true);
    }
    return this.config$.pipe(map((cfg) => cfg.verificationCachingEnabled));
  }

  getVerificationValidityDays(): Observable<number | null> {
    return this.config$.pipe(map((cfg) => cfg.verificationValidityDays));
  }

  getFeedbackConfig(): Observable<{ enabled: boolean; portalId: string | null }> {
    return this.config$.pipe(map((cfg) => ({ enabled: cfg.feedbackEnabled, portalId: cfg.feedbackPortalId })));
  }

  private extractTenantKey(hostname: string): string {
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'suppliermanagement';
    }
    const parts = hostname.split('.');
    return parts[0] || '';
  }
}
